/**
 * Supabase Client Utilities
 *
 * Provides client-side Supabase client with automatic session management.
 * Supabase handles token storage, refresh, and auth state automatically.
 */

import type { User } from '@supabase/supabase-js'
import { createClient } from '@supabase/supabase-js'
import { useEffect, useState } from 'react'
import type { Database } from './types'

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    'Missing Supabase environment variables: VITE_PUBLIC_SUPABASE_URL and VITE_PUBLIC_SUPABASE_ANON_KEY are required'
  )
}

/**
 * Client-side Supabase client for authentication and RLS-enabled queries
 *
 * Supabase automatically handles:
 * - Token storage in localStorage (key: sb-<project>-auth-token)
 * - Token refresh before expiration
 * - Session state management
 * - Auth state change events
 */
export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true, // Auto-store session in localStorage
    autoRefreshToken: true, // Auto-refresh before expiry
    detectSessionInUrl: true, // Handle OAuth callbacks
  },
  global: {
    headers: {
      'ngrok-skip-browser-warning': 'true',
    },
  },
})

/**
 * Get current session from Supabase Auth
 * Supabase automatically refreshes expired tokens
 */
export async function getSession() {
  const {
    data: { session },
    error,
  } = await supabase.auth.getSession()

  if (error) {
    console.error('[Supabase] Failed to get session:', error)
    return null
  }

  return session
}

/**
 * Sign out current user
 * Supabase automatically clears stored session
 */
export async function signOut() {
  const { error } = await supabase.auth.signOut()
  if (error) {
    console.error('[Supabase] Failed to sign out:', error)
    throw error
  }
}

/**
 * Force sign out by clearing all session data
 * Use this when normal signOut fails (e.g., session already invalid)
 */
export function forceSignOut() {
  console.log(
    '[Supabase] forceSignOut called - clearing session and reloading page'
  )

  // Clear all Supabase auth-related localStorage keys
  const keysToRemove: string[] = []
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (key && (key.startsWith('sb-') || key.includes('supabase.auth'))) {
      keysToRemove.push(key)
    }
  }
  for (const key of keysToRemove) {
    localStorage.removeItem(key)
  }

  // Dispatch storage event to trigger Supabase's internal listeners
  window.dispatchEvent(
    new StorageEvent('storage', {
      key: null,
      newValue: null,
      oldValue: null,
      storageArea: localStorage,
    })
  )

  // Force a session check to trigger auth state change
  supabase.auth.getSession().catch(() => {
    // Ignore errors - we're forcing a sign out
  })

  // Reload the page to ensure all state is reset
  // This is the most reliable way to ensure the UI updates
  window.location.reload()
}

/**
 * Get authentication token from current Supabase session
 * Returns the access token if user is logged in, null otherwise
 * Supabase automatically refreshes expired tokens
 */
export async function getAuthToken(): Promise<string | null> {
  const {
    data: { session },
    error,
  } = await supabase.auth.getSession()

  if (error || !session) {
    return null
  }

  // Supabase automatically refreshes if needed
  return session.access_token
}

/**
 * React hook for Supabase authentication
 * Use this in components to access auth state
 *
 * Supabase handles all session management automatically:
 * - Initial session check
 * - Token refresh
 * - Auth state changes
 * - Multi-tab sync
 */
export function useSupabaseAuth() {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null)
      setLoading(false)
    })

    // Listen to auth changes - Supabase handles everything!
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null)
      setLoading(false)
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [])

  return {
    user,
    loading,
    isAuthenticated: !!user,
    signOut,
  }
}

/**
 * Convert a blob URL or data URL to a File object
 */
export async function urlToFile(
  url: string,
  filename: string,
  mediaType: string
): Promise<File> {
  if (url.startsWith('data:')) {
    // Data URL: extract base64 data
    const response = await fetch(url)
    const blob = await response.blob()
    return new File([blob], filename, { type: mediaType })
  } else if (url.startsWith('blob:')) {
    // Blob URL: fetch the blob
    const response = await fetch(url)
    const blob = await response.blob()
    return new File([blob], filename, { type: mediaType })
  } else {
    // Regular URL: fetch it
    const response = await fetch(url)
    const blob = await response.blob()
    return new File([blob], filename, { type: mediaType })
  }
}

/**
 * Upload a file to the matrix-files storage bucket
 * Returns the storage path if successful, null otherwise
 */
export async function uploadFileToStorage(file: File): Promise<string | null> {
  try {
    // Verify user is authenticated before uploading
    const session = await getSession()
    if (!session) {
      console.error('[Supabase] Cannot upload file: user not authenticated')
      return null
    }

    // Generate a unique file path: timestamp + random UUID + original filename
    const timestamp = Date.now()
    const uuid = crypto.randomUUID()
    const sanitizedFilename = file.name.replace(/[^a-zA-Z0-9._-]/g, '_')
    const filePath = `${timestamp}-${uuid}-${sanitizedFilename}`

    const { data, error } = await supabase.storage
      .from('matrix-files')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false,
      })

    if (error) {
      console.error('[Supabase] Failed to upload file:', {
        message: error.message,
        error,
      })

      // Provide helpful error messages
      if (
        error.message?.includes('row-level security') ||
        error.message?.includes('RLS')
      ) {
        console.error(
          '[Supabase] RLS policy error. Ensure your RLS policy allows authenticated users to insert into the matrix-files bucket.'
        )
      }

      return null
    }

    return data.path
  } catch (error) {
    console.error('[Supabase] Error uploading file:', error)
    return null
  }
}

/**
 * Get a public URL for a file in storage
 * For private buckets, this returns a signed URL (valid for 1 hour)
 */
export async function getFileUrl(storagePath: string): Promise<string | null> {
  try {
    const { data } = await supabase.storage
      .from('matrix-files')
      .createSignedUrl(storagePath, 3600) // 1 hour expiry

    if (!data) {
      return null
    }

    return data.signedUrl
  } catch (error) {
    console.error('[Supabase] Error getting file URL:', error)
    return null
  }
}
